import { isDefined } from "@clipboard-health/util-ts";
import { environmentConfig } from "@src/appV2/environment";
import { type History } from "history";

import {
  PLACEMENT_CANDIDATES_LINK_SOURCES_PAGES,
  type PlacementCandidatesLinkSourceEvent,
  type PlacementCandidatesLinkSourcePagesKeys,
} from "./constant";

function findLinkSourcePage(linkSource: PlacementCandidatesLinkSourceEvent) {
  const pageNames = Object.keys(
    PLACEMENT_CANDIDATES_LINK_SOURCES_PAGES
  ) as PlacementCandidatesLinkSourcePagesKeys[];
  return pageNames.find((page) =>
    PLACEMENT_CANDIDATES_LINK_SOURCES_PAGES[page].includes(linkSource)
  );
}

/**
 * Builds the hire-professionals deep link for a placement candidate.
 *
 * `currentWorkplaceId` is appended as `overrideUserId`, which session init uses to
 * pseudo-log the admin in via `GET /user/get/<overrideUserId>`. That lookup only
 * resolves for the FACILITY user id; passing a workplace-user (contact) id 404s and
 * silently leaves the session as admin, so callers must pass the facility user id.
 */
export function getCandidateUrlPath(
  accessId: string,
  currentWorkplaceId: string | undefined,
  linkSource: PlacementCandidatesLinkSourceEvent,
  metadata?: Record<string, string | boolean>,
  workerType?: string | null
) {
  const metadataSource = {
    linkSourceEvent: findLinkSourcePage(linkSource),
    // FIXME: Automatically disabled during ESLint config update
    linkSourceBrowserPath: globalThis.location.pathname,
    ...metadata,
  };

  // eslint-disable-next-line no-restricted-globals
  const metadataSourceInBase64 = btoa(JSON.stringify(metadataSource));

  const searchParams = new URLSearchParams({
    noScroll: "true",
    linkSource,
    metadataSource: metadataSourceInBase64,
  });
  if (currentWorkplaceId) {
    searchParams.append("overrideUserId", currentWorkplaceId);
  }

  if (isDefined(workerType)) {
    searchParams.append("workerType", workerType);
  }

  return `/workplace/hire-professionals/${accessId}?${searchParams.toString()}`;
}

export function getCandidateUrl(
  accessId: string,
  currentWorkplaceId: string | undefined,
  linkSource: PlacementCandidatesLinkSourceEvent,
  metadata?: Record<string, string>
) {
  const urlPath = getCandidateUrlPath(accessId, currentWorkplaceId, linkSource, metadata);
  return `${environmentConfig.REACT_APP_ADMIN_APP_CLIENT_URL}${urlPath}`;
}

export function consumeUrlSearchParameter(
  parameterName: string,
  urlSearchParams: URLSearchParams,
  history: History
) {
  const parameter = urlSearchParams.get(parameterName);
  urlSearchParams.delete(parameterName);

  history.replace({
    pathname: location.pathname,
    search: urlSearchParams.toString(),
  });

  return parameter ?? undefined;
}

export function consumeMetadataSource(urlSearchParams: URLSearchParams, history: History) {
  const metadataSource = consumeUrlSearchParameter("metadataSource", urlSearchParams, history);
  if (!metadataSource) {
    return undefined;
  }

  // eslint-disable-next-line no-restricted-globals
  return JSON.parse(atob(metadataSource)) as Record<string, string>;
}
